import * as XLSX from "xlsx";

const normalizeHeader = (header) =>
  `${header || ""}`.trim().toLowerCase().replace(/\s+/g, " ");

const ALLOWED_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/octet-stream",
];

export const downloadTemplate = () => {
  const drawRows = [
    ["Loan Number", "Draw Amount", "Actual Draw Disbursement Date", "Notes"],
    ["", "", "", ""],
    ["", "", "", ""],
    ["", "", "", ""],
  ];
  const drawsSheet = XLSX.utils.aoa_to_sheet(drawRows);
  drawsSheet["!cols"] = [
    { wch: 20, z: "@" },
    { wch: 16 },
    { wch: 32, z: "mm/dd/yyyy" },
    { wch: 40 },
  ];

  const loanColIndex = 0;
  const dateColIndex = 2;
  for (let r = 1; r < drawRows.length; r += 1) {
    const loanRef = XLSX.utils.encode_cell({ c: loanColIndex, r });
    drawsSheet[loanRef] = { t: "s", v: "", z: "@" };
    const dateRef = XLSX.utils.encode_cell({ c: dateColIndex, r });
    drawsSheet[dateRef] = { t: "s", v: "", z: "mm/dd/yyyy" };
  }

  const instructionRows = [
    ["LoanLinQ – Draw Request Upload Instructions"],
    [""],
    ["Column", "Required", "Format / Rule", "Examples"],
    [
      "Loan Number",
      "Yes",
      "Must match a loan in your portfolio (Account # or Loan Rec ID). The Loan Number column in this template is pre-formatted as Text so leading zeros (e.g., 0043006545) are preserved.",
      "AHF-092121  /  0043006545",
    ],
    [
      "Draw Amount",
      "Yes",
      "Numeric and greater than 0. You may include or omit the $ sign and commas.",
      "$1,250.00  /  1250.00  /  1250",
    ],
    [
      "Actual Draw Disbursement Date",
      "Yes",
      "Must be MM/DD/YYYY. Other formats (MM-DD-YY, YYYY-MM-DD, etc.) will be rejected. Tip: format the date column as Text or Custom mm/dd/yyyy in Excel before typing values.",
      "11/04/2026",
    ],
    [
      "Notes",
      "No",
      "Optional free-text instructions for our team about this draw (e.g., how to allocate the funds). Max 500 characters.",
      "Allocate all funds",
    ],
    [""],
    ["Notes:"],
    ["• Maximum 100 rows per upload."],
    ["• Do not rename or reorder the column headers in the Draws sheet."],
    ["• Each row is submitted as one draw request."],
  ];
  const instructionsSheet = XLSX.utils.aoa_to_sheet(instructionRows);
  instructionsSheet["!cols"] = [{ wch: 18 }, { wch: 12 }, { wch: 60 }, { wch: 30 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, drawsSheet, "Draws");
  XLSX.utils.book_append_sheet(workbook, instructionsSheet, "Instructions");
  XLSX.writeFile(workbook, "draw_request_template.xlsx");
};

export const parseUploadedFile = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("Please select an .xlsx file."));
      return;
    }
    const name = `${file?.name || ""}`.toLowerCase();
    const isXlsx = name.endsWith(".xlsx");
    const mimeAllowed = !file?.type || ALLOWED_MIME_TYPES.includes(file.type);
    if (!isXlsx || !mimeAllowed) {
      reject(new Error("Only .xlsx files are allowed."));
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(new Uint8Array(event.target.result), { type: "array" });
        const sheetName = workbook.SheetNames.find((name) => normalizeHeader(name) === "draws") || workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: "" });
        const [headerRow = [], ...body] = rawRows;
        const normalized = headerRow.map(normalizeHeader);
        const dateHeaderAliases = ["actual draw disbursement date", "disbursement date", "draw date"];
        const loanIdx = normalized.indexOf("loan number");
        const amountIdx = normalized.indexOf("draw amount");
        const dateIdx = dateHeaderAliases.reduce((acc, alias) => (acc >= 0 ? acc : normalized.indexOf(alias)), -1);
        const notesIdx = normalized.indexOf("notes");
        const rows = body
          .map((entry) => {
            const rawDate = entry?.[dateIdx >= 0 ? dateIdx : 2];
            const rawLoan = entry?.[loanIdx >= 0 ? loanIdx : 0];
            const rawAmount = entry?.[amountIdx >= 0 ? amountIdx : 1];
            const rawNotes = notesIdx >= 0 ? entry?.[notesIdx] : "";
            return {
              loanNo: `${rawLoan ?? ""}`.trim(),
              drawAmount: `${rawAmount ?? ""}`.trim(),
              drawDate: rawDate ?? "",
              notes: `${rawNotes ?? ""}`.trim(),
            };
          })
          // A row with only a note (no loan/amount/date) is still empty — keep it filtered out.
          .filter((entry) => entry.loanNo || entry.drawAmount || `${entry.drawDate}`.trim());
        resolve(rows);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error("Unable to read file"));
    reader.readAsArrayBuffer(file);
  });
